import { type ThemeVersionData, validateThemeVersion } from "@klarity/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { TextArea } from "../../components/ui/Field";
import { PageTitle, Spinner } from "../../components/ui/misc";
import { toast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api/client";
import { api } from "../../lib/api/endpoints";
import { qk, useThemes } from "../../lib/queries";
import { LayoutEditor } from "./theme/LayoutEditor";
import { PreviewPane } from "./theme/PreviewPane";
import { TokenForm } from "./theme/TokenForm";
import { TOKEN_FIELDS, tokenErrors } from "./theme/token-fields";
import { useThemeDraft } from "./theme/useThemeDraft";
import { VersionsPanel } from "./theme/VersionsPanel";

type Tab = "tokens" | "layouts" | "css" | "versions";
const TABS: Array<[Tab, string]> = [
  ["tokens", "Tokens"],
  ["layouts", "Layouts"],
  ["css", "Advanced CSS"],
  ["versions", "Versions"],
];

function ErrorList({ title, errors, alert = false }: { title: string; errors: string[]; alert?: boolean }) {
  if (errors.length === 0) return null;
  return (
    <div
      role={alert ? "alert" : undefined}
      className="rounded-lg border border-danger/40 bg-danger-wash px-4 py-3 text-sm text-danger"
    >
      <p className="font-medium">{title}</p>
      <ul aria-label={alert ? undefined : "Problems"} className="mt-1 list-disc pl-5">
        {errors.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

export function ThemeEditor({ themeId }: { themeId: string }) {
  const queryClient = useQueryClient();
  const { data: themes } = useThemes();
  const theme = themes?.find((t) => t.id === themeId);
  const d = useThemeDraft(themeId);
  const { confirm, element } = useConfirm();
  const [tab, setTab] = useState<Tab>("tokens");
  const [publishErrors, setPublishErrors] = useState<string[]>([]);
  const [viewing, setViewing] = useState<string | null>(null);
  const viewed = useQuery({
    queryKey: ["themeVersion", themeId, viewing],
    queryFn: () => api.themes.version(themeId, viewing ?? ""),
    enabled: viewing !== null,
  });

  const problems = useMemo(() => {
    if (!d.data) return [];
    const r = validateThemeVersion(d.data);
    return r.ok ? [] : r.errors;
  }, [d.data]);

  if (d.isLoading || !d.data) return <Spinner label="Opening the draft…" />;
  const data: ThemeVersionData = viewing && viewed.data ? viewed.data.data : d.data;
  const readOnly = viewing !== null;
  const fieldErrors = readOnly ? {} : tokenErrors(data.tokens);
  const shown = new Set([...TOKEN_FIELDS.map((f) => f.path), "fonts.body.family", "fonts.mono.family"]);
  const orphans = Object.entries(fieldErrors).filter(([path]) => !shown.has(path));
  const orphanPaths = orphans.map(([path]) => path);
  const orphanErrors = orphans.map(([path, message]) => `${path}: ${message}`);
  const invalid = problems.length > 0 || Object.keys(fieldErrors).length > 0;
  const update = (next: ThemeVersionData) => {
    if (!readOnly) d.update(next);
  };
  const refreshAll = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.themes }),
      queryClient.invalidateQueries({ queryKey: qk.themeVersions(themeId) }),
      queryClient.invalidateQueries({ queryKey: ["themeDraft", themeId] }),
      queryClient.invalidateQueries({ queryKey: ["theme"] }),
    ]);

  const publish = async () => {
    const ok = await confirm({
      title: `Publish version ${(theme?.published?.version ?? 0) + 1}?`,
      body: "Every document that uses this theme exports with the new version from now on. The previous version stays restorable.",
      confirmLabel: "Publish version",
    });
    if (!ok) return;
    if (d.dirty && !(await d.save())) return;
    try {
      const v = await api.themes.publish(themeId);
      setPublishErrors([]);
      toast(`Version ${v.version} published.`, "success");
      await refreshAll();
    } catch (e) {
      if (e instanceof ApiError && Array.isArray(e.details.errors)) {
        setPublishErrors(e.details.errors.filter((x): x is string => typeof x === "string"));
      } else toast(`Publishing failed: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    }
  };
  const restore = async () => {
    if (!viewing || !viewed.data) return;
    const n = viewed.data.version;
    if (
      !(await confirm({
        title: `Restore version ${n}?`,
        body: "It is published again as a new version. Your open draft is not touched.",
        confirmLabel: "Restore",
      }))
    )
      return;
    try {
      const v = await api.themes.restore(themeId, viewing);
      toast(`Version ${n} restored as version ${v.version}.`, "success");
      setViewing(null);
      await refreshAll();
    } catch (e) {
      toast(`Restore failed: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    }
  };
  const uploadFont = async (file: File) => {
    try {
      d.addFont(await api.themes.uploadFont(themeId, file));
      toast(`${file.name} uploaded.`, "success");
    } catch (e) {
      toast(`${file.name} could not be uploaded: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    }
  };
  const uploadLogo = async (which: "cover" | "header", file: File) => {
    try {
      const { src } = await api.themes.uploadImage(themeId, file);
      d.update((cur) => ({ ...cur, tokens: { ...cur.tokens, logos: { ...cur.tokens.logos, [which]: src } } }));
    } catch (e) {
      toast(`${file.name} could not be uploaded: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    }
  };

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageTitle eyebrow={`Theme · draft${d.dirty ? " · unsaved changes" : ""}`}>{theme?.name ?? "Theme"}</PageTitle>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="danger"
            onClick={async () => {
              if (
                await confirm({
                  title: "Discard this draft?",
                  body: "All unpublished changes to this theme are lost.",
                  confirmLabel: "Discard draft",
                  danger: true,
                })
              )
                await d.discard();
            }}
          >
            Discard draft
          </Button>
          <Button disabled={!d.dirty || invalid} onClick={() => void d.save()}>
            Save draft
          </Button>
          <Button variant="primary" disabled={invalid} onClick={() => void publish()}>
            Publish
          </Button>
        </div>
      </div>
      <ErrorList title="Fix these before saving:" errors={orphanErrors} />
      <ErrorList
        title="Fix these before publishing:"
        errors={problems.filter((p) => !orphanPaths.some((path) => p.includes(path)))}
      />
      <ErrorList title="The draft was not saved:" errors={d.serverErrors} alert />
      <ErrorList title="Publishing failed:" errors={publishErrors} alert />
      {readOnly && viewed.data ? (
        <div className="flex items-center gap-3 rounded-lg border border-orange/50 bg-orange-wash px-4 py-2 text-sm text-orange-ink">
          <span className="flex-1">Viewing version {viewed.data.version} (read-only)</span>
          <Button size="sm" onClick={() => setViewing(null)}>
            Back to the draft
          </Button>
          <Button size="sm" aria-label={`Restore version ${viewed.data.version}`} onClick={() => void restore()}>
            Restore this version
          </Button>
        </div>
      ) : null}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <div className="grid content-start gap-4">
          <div role="tablist" aria-label="Theme parts" className="flex gap-1 border-b border-line">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={clsx(
                  "-mb-px border-b-2 px-3 py-2 text-sm",
                  tab === key ? "border-green text-green-ink" : "border-transparent text-ink-2 hover:text-ink",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === "tokens" ? (
            <TokenForm
              tokens={data.tokens}
              errors={fieldErrors}
              fonts={d.fonts}
              readOnly={readOnly}
              onChange={(tokens) => update({ ...data, tokens })}
              onUploadFont={(file) => void uploadFont(file)}
              onUploadLogo={(which, file) => void uploadLogo(which, file)}
            />
          ) : null}
          {tab === "layouts" ? (
            <LayoutEditor
              layouts={data.layouts}
              readOnly={readOnly}
              onChange={(layouts) => update({ ...data, layouts })}
            />
          ) : null}
          {tab === "css" ? (
            <div className="grid gap-2">
              <label htmlFor="advanced-css" className="text-sm font-medium text-ink-2">
                Advanced CSS (admins only)
              </label>
              <p className="text-xs text-ink-3">
                Appended last to the print CSS of PDF exports. Word exports ignore it. @import and remote URLs are
                refused.
              </p>
              <TextArea
                id="advanced-css"
                rows={20}
                spellCheck={false}
                readOnly={readOnly}
                value={data.advancedCss}
                onChange={(e) => update({ ...data, advancedCss: e.target.value })}
              />
            </div>
          ) : null}
          {tab === "versions" ? (
            <VersionsPanel
              themeId={themeId}
              viewing={viewing}
              onView={(id) => {
                setViewing(id);
                if (id) setTab("tokens");
              }}
            />
          ) : null}
        </div>
        <PreviewPane
          themeId={themeId}
          layoutKeys={Object.keys(d.data.layouts)}
          beforeRender={async () => (d.dirty ? d.save() : true)}
        />
      </div>
      {element}
    </div>
  );
}
